// File: src/components/music/ReactionSelector.js

import React, { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import likedIcon from '../../assets/trends_liked.png';
import dislikedIcon from '../../assets/trends_disliked.png';

/**
 * ReactionSelector Pill Popover
 */
const ReactionSelector = ({ 
    isOpen, 
    onClose, 
    onSelectReaction, 
    isDarkMode, 
    position = 'top',
    onMouseEnter // 🌟 Added: Notifies parent when mouse enters popover
}) => {
    const popoverRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const positionClasses = position === 'left'
        ? 'right-12 top-1/2 -translate-y-1/2'
        : 'bottom-11 left-1/2 -translate-x-1/2';

    const motionInitial = position === 'left' ? { scale: 0.8, opacity: 0, x: 10 } : { scale: 0.8, opacity: 0, y: 6 };
    const motionAnimate = position === 'left' ? { scale: 1, opacity: 1, x: 0 } : { scale: 1, opacity: 1, y: 0 };
    const motionExit = position === 'left' ? { scale: 0.8, opacity: 0, x: 10 } : { scale: 0.8, opacity: 0, y: 6 };

    return (
        <AnimatePresence>
            <motion.div
                ref={popoverRef}
                initial={motionInitial}
                animate={motionAnimate}
                exit={motionExit}
                transition={{ type: 'spring', stiffness: 450, damping: 25 }}
                onMouseEnter={onMouseEnter} // 🔑 Cancels dismissal when cursor moves into popover
                className={`absolute z-[100] flex items-center gap-2 px-3 py-2 rounded-full border shadow-2xl ${positionClasses} ${
                    isDarkMode 
                        ? 'bg-zinc-950/95 border-zinc-800 text-white shadow-black/90' 
                        : 'bg-white/95 border-slate-200 text-slate-900 shadow-slate-300'
                } backdrop-blur-md`}
            >
                {/* 1. HOT (LIKE) BUTTON */}
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onSelectReaction('liked');
                        onClose();
                    }}
                    className="flex flex-col items-center justify-center gap-0.5 px-2 py-0.5 rounded-full hover:bg-pink-500/10 transition-transform active:scale-110 cursor-pointer"
                >
                    <img src={likedIcon} alt="Hot" className="w-5 h-5 object-contain" />
                    <span className="text-[8px] font-black uppercase tracking-wider text-[#ff00cc]">
                        Hot
                    </span>
                </button>

                {/* Vertical Divider */}
                <div className={`w-[1px] h-6 ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-200'}`} />

                {/* 2. NOT (DISLIKE) BUTTON */}
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onSelectReaction('disliked');
                        onClose();
                    }}
                    className="flex flex-col items-center justify-center gap-0.5 px-2 py-0.5 rounded-full hover:bg-slate-500/10 transition-transform active:scale-110 cursor-pointer"
                >
                    <img src={dislikedIcon} alt="Not" className="w-5 h-5 object-contain" />
                    <span className="text-[8px] font-black uppercase tracking-wider text-slate-400">
                        Not
                    </span>
                </button>
            </motion.div>
        </AnimatePresence>
    );
};

export default ReactionSelector;